import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Header, PageType } from './components/Header';
import { Footer } from './components/Footer';
import { Hero } from './components/Hero';
import { AboutSection } from './components/AboutSection';
import { ProductCategories } from './components/ProductCategories';
import { ClientLogosMarquee } from './components/ClientLogosMarquee';
import { ManufacturingCapabilities } from './components/ManufacturingCapabilities';
import { ContactForm } from './components/ContactForm';
import { ProductCategoryPage } from './components/ProductCategoryPage';
import { AboutPage } from './components/AboutPage';
import { InfrastructurePage } from './components/InfrastructurePage';
import { NewsPage } from './components/NewsPage';
import { useTheme } from './contexts/ThemeContext';
import { productCategories } from './data/products';

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<PageType>('home');
  const { isDark } = useTheme();

  const handleNavigate = (page: PageType) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  // Check if current page is a product category page
  const productCategoryKey = ['steel-plant', 'cement-plant', 'mineral-handling', 'defence', 'renewable-energy', 'boiler-components'].find(
    (key) => key === currentPage
  );
  const currentProductCategory = productCategoryKey ? productCategories[productCategoryKey] : null;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-vmil-navy' : 'bg-vmil-light'
      }`}>
      {/* Header */}
      <Header currentPage={currentPage} onNavigate={handleNavigate} />

      {/* Main Content */}
      <main>
        {currentPage === 'home' && (
          <>
            <Hero onNavigate={handleNavigate} />

            {/* Client Logos - Trusted by Global Industry Leaders */}
            <ClientLogosMarquee />

            {/* About Section */}
            <AboutSection onNavigate={handleNavigate} />

            {/* Our Manufacturing Capabilities */}
            <ManufacturingCapabilities onNavigate={handleNavigate} />

            {/* Our Industries */}
            <section id="our-products" className={`w-full pt-16 md:pt-24 pb-16 md:pb-24 relative overflow-hidden transition-colors duration-300 ${isDark ? 'bg-gradient-to-br from-vmil-navy via-vmil-navy to-vmil-blue/30' : 'bg-gradient-to-br from-gray-50 via-white to-vmil-blue/5'
              }`}>
              {/* Decorative Elements */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className={`absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl ${isDark ? 'bg-vmil-blue/10' : 'bg-vmil-blue/10'}`} />
                <div className={`absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl ${isDark ? 'bg-vmil-blue/20' : 'bg-vmil-blue/10'}`} />
              </div>
              <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center mb-4 md:mb-6">
                  <div className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border mb-3 ${isDark ? 'bg-vmil-blue/10 border-vmil-blue/20' : 'bg-vmil-blue/10 border-vmil-blue/20'
                    }`}>
                    <span className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-vmil-blue">
                      Browse by Sector
                    </span>
                  </div>
                  <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                    Our Industries
                  </h2>
                  <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-white/80' : 'text-vmil-steel'}`}>
                    Browse by industry to explore our specialized equipment, technical capabilities, and comprehensive product catalogues tailored to each sector.
                  </p>
                </div>
                <div className="mt-8">
                  <ProductCategories onNavigate={handleNavigate} compact />
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="relative w-full py-12 sm:py-16 md:py-24 overflow-hidden">
              <div className={`absolute inset-0 ${isDark
                  ? 'bg-gradient-to-br from-vmil-blue via-vmil-navy to-vmil-blue'
                  : 'bg-gradient-to-br from-vmil-blue via-vmil-navy to-vmil-blue'
                }`} />

              {/* Animated Elements */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-10 right-10 sm:top-20 sm:right-20 w-40 h-40 sm:w-72 sm:h-72 bg-vmil-blue/10 rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-10 left-10 sm:bottom-20 sm:left-20 w-48 h-48 sm:w-96 sm:h-96 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
              </div>

              <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                  <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto">
                    With over 45 years of experience in manufacturing equipment for core industries,
                    we're ready to help bring your project to life.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                    <button
                      onClick={() => handleNavigate('contact')}
                      className="inline-flex h-12 sm:h-14 items-center justify-center rounded-xl bg-vmil-blue px-6 sm:px-8 text-base sm:text-lg font-bold text-white shadow-lg shadow-vmil-blue/25 transition-all hover:shadow-xl hover:shadow-vmil-blue/30 hover:scale-[1.02]"
                    >
                      Get in Touch
                    </button>
                    <a
                      href="tel:+914312500547"
                      className="inline-flex h-12 sm:h-14 items-center justify-center rounded-xl border-2 border-white/30 px-6 sm:px-8 text-base sm:text-lg font-bold text-white transition-all hover:bg-white/10 hover:border-white/50"
                    >
                      Call Us: +91 431 2500 547
                    </a>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}

        {currentPage === 'about' && <AboutPage onNavigate={handleNavigate} />}

        {currentPage === 'infrastructure' && (
          <InfrastructurePage onNavigate={handleNavigate} onBack={() => handleNavigate('home')} />
        )}

        {currentPage === 'news' && <NewsPage onBack={() => handleNavigate('home')} />}

        {currentPage === 'products' && (
          <>
            {/* Hero */}
            <section className="relative w-full py-20 sm:py-28 md:py-36 overflow-hidden">
              <div className="absolute inset-0">
                <img
                  src="/factory-1.jpg"
                  alt="VMIL Industries"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-vmil-navy/90 via-vmil-navy/70 to-transparent" />
              </div>
              <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="max-w-2xl">
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
                    Industries
                  </h1>
                  <p className="text-lg text-white/85 leading-relaxed">
                    Industrial equipment for steel plants, cement plants, mineral handling, defence, boiler components, and renewable energy. Each industry features detailed information and a product catalogue.
                  </p>
                </div>
              </div>
            </section>

            {/* Infrastructure & Facility */}
            <section className={`w-full py-16 md:py-24 ${isDark ? 'bg-vmil-navy' : 'bg-vmil-light'}`}>
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-6 ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                  Infrastructure & Facility
                </h2>
                <p className={`text-base sm:text-lg max-w-3xl ${isDark ? 'text-white/90' : 'text-vmil-steel'}`}>
                  From design to delivery: we fabricate, machine, test, and deliver heavy fabrications and precision-machined components from 5 to 250 tonnes. Certified for over 25 years, with exports to Malaysia, Australia, UAE, USA, and Europe since 2001.
                </p>
                <button
                  onClick={() => handleNavigate('infrastructure')}
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-vmil-blue px-6 py-3 text-white font-semibold hover:bg-vmil-blue/90 transition-colors"
                >
                  Full infrastructure details <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </section>

            {/* Featured Equipment */}
            <section className={`w-full py-16 md:py-24 ${isDark ? 'bg-white/5' : 'bg-white'}`}>
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className={`text-xl sm:text-2xl font-bold mb-10 text-center ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                  Featured Equipment
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 max-w-4xl mx-auto">
                  {[
                    { src: '/products/steel-plant/Lance-Trolley.jpg', title: 'Lance Trolley' },
                    { src: '/products/steel-plant/Tilting-platform.jpg', title: 'Tilting Platform' },
                    { src: '/products/mineral-handling/Stacker-Reclaimer.jpg', title: 'Stacker Reclaimer' },
                    { src: '/products/cement-plant/hrp-frame_2-1.jpg', title: 'HRP Frame' },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className={`rounded-xl overflow-hidden group ${isDark ? 'bg-white/5' : 'bg-vmil-light'
                        }`}
                    >
                      <div className="aspect-square overflow-hidden">
                        <img
                          src={item.src}
                          alt={item.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                      <p className={`p-3 sm:p-4 text-center text-sm font-medium ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                        {item.title}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}

        {currentPage === 'contact' && (
          <>
            {/* Contact Hero with Google Maps Background */}
            <section className="relative w-full py-16 sm:py-24 md:py-32 overflow-hidden">
              <div className="absolute inset-0">
                <iframe
                  src="https://maps.google.com/maps?q=Developed%20Plot%20Estate%2C%20E53%2C54%2C%20Thuvakudi%2C%20Tamil%20Nadu%20620015%2C%20India&t=m&z=14&output=embed"
                  title="VMIL Location"
                  className="w-full h-full"
                  loading="lazy"
                  style={{ filter: 'grayscale(30%)' }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-vmil-navy/90 via-vmil-navy/70 to-vmil-navy/50" />
              </div>
              <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-3xl">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white mb-4 sm:mb-6">
                    Contact
                  </h1>
                  <p className="text-base sm:text-lg md:text-xl text-white/80">
                    Get in touch with us to discuss your industrial equipment requirements.
                    Our team is ready to help.
                  </p>
                </div>
              </div>
            </section>

            <ContactForm />
          </>
        )}

        {/* Product Category Pages */}
        {currentProductCategory && (
          <ProductCategoryPage
            title={currentProductCategory.title}
            subtitle={currentProductCategory.subtitle}
            heroImage={currentProductCategory.heroImage}
            products={currentProductCategory.products}
            onBack={() => handleNavigate('products')}
          />
        )}
      </main>

      {/* Footer */}
      <Footer onNavigate={handleNavigate} />
    </div>
  );
};

export default App;
